import { captureMessage, withScope } from '@sentry/nextjs';

import { logger } from '@/lib/logger';

type RecordingStartEvent = 'start_clicked' | 'source_selected' | 'source_cancelled' | 'source_failed' | 'recording_started' | 'start_failed';

/** 音声本文・画面内容・入力先ラベルを含まない試行単位の診断イベント。 */
export function logRecordingStartEvent(event: RecordingStartEvent, attemptId: string, detail: {
  meetingId?: string | null;
  errorKind?: string;
} = {}): void {
  logger.info(`[RecordingStart] ${event}`, { attemptId, ...detail });
  withScope((scope) => {
    scope.clearBreadcrumbs();
    captureMessage('Recording start attempt', {
      level: 'info',
      tags: { operation: 'recording_start', stage: event, attemptId },
      extra: detail,
    });
  });
}

/** ユーザー操作中に音声出力を準備し、録音確認成功後だけ短い開始音を鳴らす。 */
export function createRecordingStartSignal(): { play: () => void; dispose: () => void } {
  let context: AudioContext | null = null;
  try {
    if (typeof window !== 'undefined' && typeof AudioContext !== 'undefined') {
      context = new AudioContext();
      void context.resume().catch(() => {});
    }
  } catch { /* 通知音の失敗で録音開始を妨げない。 */ }

  const dispose = () => {
    if (context && context.state !== 'closed') void context.close().catch(() => {});
  };
  return {
    dispose,
    play: () => {
      if (!context) return;
      try {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        oscillator.frequency.value = 880;
        gain.gain.setValueAtTime(0.06, context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.15);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.onended = dispose;
        oscillator.start();
        oscillator.stop(context.currentTime + 0.15);
      } catch { dispose(); }
    },
  };
}
